import { createActorContext } from '@xstate/react';
import axios, { AxiosError } from 'axios';
import { assign, createMachine } from 'xstate';

import { passkeysController } from 'pages/SignIn/passkeysController';
import { ErrorData } from 'pages/SignIn/types';
import { getEmailSSOProvider } from 'pages/SignIn/utils';
import { authenticationBus } from 'useAuthorizationWatcher';
import { doRedirect, getCsrfToken } from 'utils/auth';
import {
  API_ENDPOINTS,
  STORAGE_KEY_PASSKEY_LOGIN,
  STORAGE_KEY_SSO_PROVIDER
} from 'utils/constants';
import { RedirectData, SSOProvider } from 'utils/types';

type MachineContext = {
  email: string;
  password: string;
  otp: string;
  isOtpRequired: boolean;
  data: RedirectData | null;
  errorData: ErrorData | null;
  isUnlockAccount: boolean;
  unlockAccountToken: string;
  locale: string;
};

export const signInMachine = createMachine(
  {
    predictableActionArguments: true,
    preserveActionOrder: true,
    tsTypes: {} as import('./machine.typegen').Typegen0,
    schema: {
      context: {} as MachineContext,
      events: {} as
        | { type: 'Set Email'; email: string }
        | { type: 'Set Password'; password: string }
        | { type: 'Set OTP'; otp: string }
        | { type: 'Create Passkey' }
        | { type: 'Submit Form' }
        | { type: 'Log in with Passkey' },
      services: {} as {
        createPasskey: { data: RedirectData | null };
        loginWithExistingPasskey: { data: RedirectData | null };
        submitForm: { data: RedirectData | null };
      }
    },
    id: 'Sign In Machine',
    initial: 'initial',
    states: {
      initial: {
        always: [
          { target: 'unlock', cond: 'isUnlockAccount' },
          { target: 'unlocked' }
        ]
      },
      unlock: {
        id: 'Unlock Machine',
        initial: 'loading',
        states: {
          loading: {
            invoke: {
              id: 'unlockAccount',
              src: 'unlockAccount',
              onDone: {
                target: '#Sign In Machine.unlocked'
              },
              onError: {
                target: 'failed'
              }
            }
          },
          failed: {}
        }
      },
      unlocked: {
        type: 'parallel',
        states: {
          passkeyLogin: {
            initial: 'idle',
            on: {
              'Log in with Passkey': { target: '.loginWithExistingPasskey' }
            },
            states: {
              idle: {
                always: [
                  {
                    target: 'loginWithExistingPasskey',
                    cond: 'isPasskeyLoginKeySet'
                  }
                ]
              },
              loginWithExistingPasskey: {
                invoke: {
                  id: 'loginWithExistingPasskey',
                  src: 'loginWithExistingPasskey',
                  onDone: {
                    target: '#Sign In Machine.loggedIn',
                    actions: [
                      'setData',
                      'enablePasskeyLoginLocalStorageKey',
                      'redirect'
                    ]
                  },
                  onError: {
                    target: 'failed',
                    actions: ['disablePasskeyLoginLocalStorageKey']
                  }
                }
              },
              failed: {}
            }
          },
          passwordLogin: {
            initial: 'pristine',
            states: {
              pristine: {
                tags: ['editableEmail', 'editablePassword'],
                on: {
                  'Set Email': { target: 'dirty', actions: ['setEmail'] },
                  'Set Password': { target: 'dirty', actions: ['setPassword'] }
                }
              },
              dirty: {
                tags: ['editableEmail', 'editablePassword'],
                on: {
                  'Set Email': { target: 'dirty', actions: ['setEmail'] },
                  'Set Password': { target: 'dirty', actions: ['setPassword'] },
                  'Submit Form': { target: 'submitting' }
                }
              },
              otpRequired: {
                tags: ['editableOtp'],
                on: {
                  'Set OTP': { target: 'otpRequired', actions: ['setOTP'] },
                  'Submit Form': { target: 'submitting' }
                }
              },
              submitting: {
                invoke: {
                  id: 'submitForm',
                  src: 'submitForm',
                  onDone: {
                    target: '#Sign In Machine.loggedIn',
                    actions: ['setData', 'redirect']
                  },
                  onError: [
                    {
                      target: '#Sign In Machine.expiringPassword',
                      cond: 'isPasswordExpiring',
                      actions: ['setError']
                    },
                    {
                      target: '#Sign In Machine.expiredPassword',
                      cond: 'isPasswordExpired',
                      actions: ['setError']
                    },
                    {
                      target: '#Sign In Machine.usePasskeys',
                      cond: 'isPasskeysAvailable',
                      actions: ['setError']
                    },
                    {
                      target: 'dirty',
                      cond: 'isSSORequired',
                      actions: ['setError', 'loginWithSSOProvider']
                    },
                    {
                      target: 'otpRequired',
                      cond: 'isOTPRequired',
                      actions: ['setIsOtpRequired', 'setError']
                    },
                    {
                      target: 'dirty',
                      actions: ['setError']
                    }
                  ]
                }
              }
            }
          }
        }
      },
      loggedIn: {},
      expiringPassword: {},
      expiredPassword: {},
      usePasskeys: {
        on: { 'Create Passkey': '.creatingPasskey' },
        initial: 'idle',
        states: {
          idle: {},
          creatingPasskey: {
            invoke: {
              id: 'createPasskey',
              src: 'createPasskey',
              onDone: {
                target: 'done',
                actions: ['enablePasskeyLoginLocalStorageKey']
              },
              onError: {
                target: 'failed'
              }
            }
          },
          done: {},
          failed: {}
        }
      }
    }
  },
  {
    guards: {
      isUnlockAccount: (context) => context.isUnlockAccount,
      isPasswordExpiring: (_, event) =>
        (event.data as AxiosError<{ error: string }>).response?.data.error ===
        'password_expiring',
      isPasswordExpired: (_, event) =>
        (event.data as AxiosError<{ error: string }>).response?.data.error ===
          'password_expired' ||
        (event.data as AxiosError<{ error: string }>).response?.data.error ===
          'password_uninitialized',
      isOTPRequired: (context, event) =>
        (event.data as AxiosError<{ error: string }>).response?.data.error ===
          'otp_required' || context.isOtpRequired,
      isSSORequired: (_, event) =>
        (event.data as AxiosError<{ error: string }>).response?.data.error ===
        'sso_required',
      isPasskeysAvailable: (_, event) =>
        (event.data as AxiosError<{ error: string }>).response?.data.error ===
        'use_passkeys',
      isPasskeyLoginKeySet: () =>
        localStorage.getItem(STORAGE_KEY_PASSKEY_LOGIN) === 'true'
    },
    actions: {
      setEmail: assign((_, event) => ({
        email: event.email
      })),
      setPassword: assign((_, event) => ({ password: event.password })),
      setOTP: assign((_, event) => ({ otp: event.otp })),
      setIsOtpRequired: assign(
        () =>
          ({
            isOtpRequired: true
          } as MachineContext)
      ),
      setData: assign((_, event) => ({
        data: event.data
      })),
      redirect: (context) => {
        if (context.data) {
          authenticationBus.sendMessage();
          doRedirect(context.data.location);
        }
      },
      enablePasskeyLoginLocalStorageKey: () => {
        localStorage.setItem(STORAGE_KEY_PASSKEY_LOGIN, 'true');
      },
      disablePasskeyLoginLocalStorageKey: (_, event) => {
        if ((event.data as Error).message === 'rejected_or_timeout') return;
        localStorage.setItem(STORAGE_KEY_PASSKEY_LOGIN, 'false');
      },
      setError: assign((_, event) => {
        if (
          axios.isAxiosError(event.data) &&
          event.data instanceof AxiosError
        ) {
          return {
            data: null,
            errorData: event.data.response?.data
          };
        }
        return { data: null };
      }),
      loginWithSSOProvider: async (context) => {
        let provider: SSOProvider | null = null;
        provider = await getEmailSSOProvider(context.email);
        if (provider) {
          authenticationBus.sendMessage();
          localStorage.setItem(STORAGE_KEY_SSO_PROVIDER, provider.key);
          doRedirect(provider.url);
        }
      }
    },
    services: {
      unlockAccount: async (context) =>
        axios({
          method: 'put',
          url: API_ENDPOINTS.UNLOCK_ACCOUNT,
          data: {
            token: context.unlockAccountToken || '',
            locale: context.locale,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            _csrf_token: getCsrfToken()
          },
          headers: {
            accept: 'application/json'
          }
        }),
      createPasskey: () => passkeysController.createPasskey(),
      loginWithExistingPasskey: () =>
        passkeysController.loginWithExistingPasskey(),
      submitForm: async ({ email, password, otp, locale }) =>
        axios({
          method: 'post',
          url: API_ENDPOINTS.SIGN_IN,
          data: {
            email,
            password,
            otp,
            locale
          },
          headers: {
            accept: 'application/json',
            'x-csrf-token': getCsrfToken()
          }
        }).then(({ data }) => data)
    }
  }
);

export const SignInPageController = createActorContext(signInMachine, {
  devTools: CONFIG.isDevelopment
});
